/** @typedef {import("@/services/ContextDetectionService").MenuState} MenuState */
/** @typedef {import("@/services/ContextDetectionService").MenuContext} MenuContext */
import LayoutChooserActions from "@/actions/LayoutChooserActions";
import AppConstants from "@/constants/AppConstants";
import ModelFactory from "@/models/ModelFactory";
import SiteActions from "@/actions/SiteActions";
import DocumentStore from "@/document/DocumentStore";
import { COMMANDS } from "@/document/commands";
import UIActions from "@/actions/UIActions";
import BlockUtils from "@/utils/BlockUtils";
import IDUtils from "@/utils/IDUtils";
import GroupStore from "@/stores/GroupStore";
import {
  addSelectionToGroup,
  removeSelectionFromGroup,
  groupSelection,
  dissolveSelection,
} from "@/utils/GroupUtils";
import { moveElementLayers, moveElementsToBound } from "@/utils/LayerUtils";
import {
  LAYER_BOUND_FRONT,
  LAYER_BOUND_BACK,
  DIRECTION_DOWN,
  DIRECTION_UP,
} from "@/constants/LayerConstants";

import {
  UNDO,
  REDO,
  COPY,
  CUT,
  PASTE_ELEMENT,
  DELETE_ELEMENT,
  DUPLICATE_ELEMENT,
  RENAME_ELEMENT,
  TOGGLE_LOCK_ELEMENT,
  SELECT_CANVAS,
  SELECT_SITE_CANVAS,
  DELETE_CANVAS,
  RENAME_CANVAS,
  LAYOUT_MOBILE_CANVAS,
  DUPLICATE_CANVAS,
  ADD_TEXT_ELEMENT_TITLE,
  ADD_TEXT_ELEMENT_HEADING,
  ADD_TEXT_ELEMENT_SUBHEADING,
  ADD_TEXT_ELEMENT_PARAGRAPH,
  ALIGN_ELEMENT_TO_CANVAS_HORIZONTAL,
  ALIGN_ELEMENT_TO_CANVAS_VERTICAL,
  ADD_ELEMENT_SIMPLE,
  ADD_ELEMENT_OVAL,
  ADD_ELEMENT_LINE,
  ADD_ELEMENT_GRAPHIC,
  ADD_ELEMENT_ICON,
  ADD_ELEMENT_IFRAME,
  ADD_ELEMENT_VIDEO,
  ADD_ELEMENT_GALLERY,
  ADD_ELEMENT_SOCIAL_GRID,
  ADD_ELEMENT_LOTTIE,
  ADD_ELEMENT_BUTTON,
  ADD_FORM_SUBMIT_BUTTON,
  ALIGN_ELEMENT_TO_CANVAS_TOP,
  ALIGN_ELEMENT_TO_CANVAS_BOTTOM,
  ALIGN_ELEMENT_TO_CANVAS_LEFT,
  ALIGN_ELEMENT_TO_CANVAS_RIGHT,
  ALIGN_SELECTED_ELEMENTS_HORIZONTAL,
  ALIGN_SELECTED_ELEMENTS_VERTICAL,
  ALIGN_SELECTED_ELEMENTS_TOP,
  ALIGN_SELECTED_ELEMENTS_BOTTOM,
  ALIGN_SELECTED_ELEMENTS_LEFT,
  ALIGN_SELECTED_ELEMENTS_RIGHT,
  DISTRIBUTE_ELEMENTS_HORIZONTAL,
  DISTRIBUTE_ELEMENTS_VERTICAL,
  BRING_TO_FRONT,
  BRING_FORWARD,
  SEND_BACKWARD,
  SEND_TO_BACK,
  SWAP_IMAGE,
  RESET_IMAGE,
  CROP_IMAGE,
  UNGROUP_ELEMENTS,
  GROUP_ELEMENTS,
  ADD_TO_GROUP,
  REMOVE_FROM_GROUP,
  INSPECT_ELEMENT_JSON,
} from "@/constants/ActionConstants";
import TransformActions from "@/actions/TransformActions";
import MediaSelectHandler from "@/components/utils/MediaSelectHandler";
import ImageActions from "@/actions/ImageActions";
import SelectionStore from "@/stores/SelectionStore";
import { FORM_FIELD_MENU_ITEMS } from "@/constants/FormFieldConstants.mjs";

const {
  ITEM_TYPE_BLOCK,
  ITEM_TYPE_ELEMENT,
  ELEMENT_TYPE_SOCIAL_GRID,
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_ICON,
  ELEMENT_TYPE_IFRAME,
  ELEMENT_TYPE_VIDEO,
  ELEMENT_TYPE_GALLERY,
  ELEMENT_TYPE_GRAPHIC,
  ELEMENT_TYPE_BUTTON,
  ELEMENT_TYPE_LOTTIE,
  ELEMENT_TYPE_OVAL,
  TEXT_STYLE_TITLE,
  TEXT_STYLE_HEADING,
  TEXT_STYLE_SUBHEADING,
  TEXT_STYLE_PARAGRAPH,
  DESIGN_ITEM_PROP_PARENT_TYPE,
  SITE_ITEM_SITE,
  MODAL_ACTION_CONFIRM,
  ALIGN_CENTER,
  ALIGN_MIDDLE,
  ALIGN_TOP,
  ALIGN_BOTTOM,
  ALIGN_LEFT,
  ALIGN_RIGHT,
  DISTRIBUTE_HORIZONTAL_SPACE,
  DISTRIBUTE_VERTICAL_SPACE,
  BLOCK_PROP_PARENT_BLOCK,
} = AppConstants;

const _pasteElement = ({ onClose, canvasPosition }) => {
  DocumentStore.dispatch({ type: COMMANDS.PASTE, payload: { canvasPosition } });
  onClose();
};

const _removeBlock = ({ onClose, entity }) => {
  const isSiteBlock = entity.get(DESIGN_ITEM_PROP_PARENT_TYPE) === SITE_ITEM_SITE;
  if (isSiteBlock) {
    UIActions.modalOpen(MODAL_ACTION_CONFIRM, {
      title: "Delete Site Canvas",
      question: "Are you sure you want to delete this site canvas?",
      message:
        "This removes a site canvas from design and will affect all pages the canvas was used on.",
      confirmBtn: "Delete",
      confirmAction: () =>
        DocumentStore.dispatch({ type: COMMANDS.REMOVE_SITE_BLOCK, payload: { block: entity } }),
    });
  } else {
    DocumentStore.dispatch({ type: COMMANDS.REMOVE_BLOCK, payload: { block: entity } });
  }
  onClose();
};

const _duplicateBlock = ({ onClose, entity }) => {
  DocumentStore.dispatch({ type: COMMANDS.DUPLICATE_BLOCK, payload: { id: entity.get("id") } });
  onClose();
};

const _layoutMobile = ({ onClose, entity }) => {
  LayoutChooserActions.openDialog({ id: entity.get("id") });
  onClose();
};

const _renameBlock = ({ onClose, entity }) => {
  const isSiteBlock = BlockUtils.isSiteBlock(entity);

  UIActions.toggleRenamePopup({
    title: `${isSiteBlock ? "Site " : ""}Canvas`,
    itemType: ITEM_TYPE_BLOCK,
    itemId: entity.get("id"),
    name: entity.get("name"),
    slug: entity.get("slug"),
    showWarning: true,
  });
  onClose();
};

const _selectSiteBlock = ({ onClose, entity, context }) => {
  const baseBlock = entity.get(BLOCK_PROP_PARENT_BLOCK);

  UIActions.selectSiteBlock({ block: baseBlock });
  UIActions.addActiveItemHistory({
    type: ITEM_TYPE_BLOCK,
  });
  onClose();
};

const _selectBlock = ({ onClose, context, layout }) => {
  const { unselectedBlockId } = context;
  UIActions.selectBlock({ id: unselectedBlockId, layout });
  UIActions.scrollIntoView({ id: unselectedBlockId, type: "block" });
  onClose();
};

const _undo = ({ onClose }) => {
  DocumentStore.dispatch({ type: COMMANDS.UNDO });
  onClose();
};

const _redo = ({ onClose }) => {
  DocumentStore.dispatch({ type: COMMANDS.REDO });
  onClose();
};

const _copy = ({ onClose }) => {
  SiteActions.copy({});
  onClose();
};

const _cut = ({ onClose }) => {
  SiteActions.copy({ cut: true });
  onClose();
};

const _removeElement = ({ onClose }) => {
  DocumentStore.dispatch({ type: COMMANDS.REMOVE_SELECTED_ELEMENTS });
  onClose();
};

const _alignElementToCanvas = ({ onClose }, alignDirection) => {
  TransformActions.alignToCanvas(alignDirection);
  onClose();
};

const _alignSelectedElements = ({ onClose }, alignDirection) => {
  TransformActions.align(alignDirection);
  onClose();
};

const _distributeElements = ({ onClose }, distributeDirection) => {
  TransformActions.distributeSpace(distributeDirection);
  onClose();
};

const _duplicateElement = ({ onClose }) => {
  SiteActions.copy({});
  // Necessary to wait for the copy to complete before pasting
  setTimeout(() => DocumentStore.dispatch({ type: COMMANDS.PASTE, payload: {} }), 100);
  onClose();
};

const _swapImage = ({ onClose, entity }) => {
  MediaSelectHandler.selectMedia(entity.get("id"), entity, { type: "image" });
  onClose();
};

const _resetImage = ({ onClose, entity }) => {
  const aspectRatio = entity.getIn(["content", "aspect_ratio"], false);
  DocumentStore.dispatch({
    type: COMMANDS.RESIZE_TO_ASPECT_RATIO,
    payload: [entity, entity.get("id"), aspectRatio],
  });
  onClose();
};

const _cropImage = ({ onClose }) => {
  const selectedBlock = SelectionStore.getBlock();
  const selectedElements = selectedBlock.getIn(["selected", "elements"]);
  if (selectedElements && selectedElements.size > 0) {
    const selectedElementData = selectedElements
      .map((elemInfo) => {
        return selectedBlock.getIn(IDUtils.getArray(elemInfo.id, 1));
      })
      .first();
    ImageActions.enterCropMode(selectedElementData, selectedBlock);
  }

  onClose();
};

const _getSelected = () => {
  const selectedBlock = SelectionStore.getBlock();
  const selectedElements = selectedBlock.getIn(["selected", "elements"]);
  return [selectedElements, selectedBlock];
};

const _addToGroup = ({ onClose }) => {
  const [selectedElements, selectedBlock] = _getSelected();
  addSelectionToGroup(selectedElements, selectedBlock);
  onClose();
};

const _removeFromGroup = ({ onClose }) => {
  const [selectedElements, selectedBlock] = _getSelected();
  removeSelectionFromGroup(selectedElements, selectedBlock);
  onClose();
};

const _ungroupElements = ({ onClose }) => {
  const [selectedElements, selectedBlock] = _getSelected();
  dissolveSelection(selectedElements, selectedBlock);
  onClose();
};

const _groupElements = ({ onClose }) => {
  const [selectedElements, selectedBlock] = _getSelected();
  groupSelection(selectedElements, selectedBlock);
  onClose();
};

const _inspectElementJSON = ({ onClose }) => {
  const elementIds = SelectionStore.getElementIds();
  if (elementIds.length === 1) {
    const idPath = IDUtils.getArray(elementIds[0]);
    const tab = BlockUtils.isSiteBlock(SelectionStore.getBlock()) ? "site" : "page";
    import("@/components/JSONEditor")
      .then(({ setInspectElementContext }) => {
        setInspectElementContext({ idPath, tab });
        UIActions.toggleJSONEditor();
      })
      .catch((e) => console.error("[InspectElementJSON] Failed to load editor:", e));
  }
  onClose();
};

const _renameElement = ({ onClose, entity }) => {
  UIActions.toggleRenamePopup({
    title: "Element",
    itemType: ITEM_TYPE_ELEMENT,
    itemId: entity.get("id"),
    name: entity.get("name"),
    slug: entity.get("slug"),
  });
  onClose();
};

const _toggleLock = ({ onClose }) => {
  DocumentStore.dispatch({ type: COMMANDS.TOGGLE_ITEMS_LOCK_STATUS });
  onClose();
};

const _moveLayers = ({ onClose }, direction) => {
  moveElementLayers(direction);
  onClose();
};

const _moveToBound = ({ onClose }, bound) => {
  moveElementsToBound(bound);
  onClose();
};

/**
 * Adds a freshly built element at the right-clicked point, offset into the selected group.
 * @param {Object} props - Context menu action props.
 * @param {string} props.layout - The layout the menu was opened on.
 * @param {string} props.otherLayout - The other layout.
 * @param {MenuState["position"]} [props.canvasPosition] - The right-clicked point in each layout.
 * @param {() => void} props.onClose - Closes the menu.
 * @param {MenuContext} [props.context] - The right-click target's context.
 * @param {Object} element - A new element from `ModelFactory`.
 */
const _insertElementAtCursor = (
  { layout, canvasPosition, otherLayout, onClose, context },
  element,
) => {
  const containedBy = GroupStore.getGroupSelectionId();
  if (element[layout] && canvasPosition) {
    let offsetX = 0;
    let offsetY = 0;
    let otherOffsetX = 0;
    let otherOffsetY = 0;
    if (containedBy && context?.selectedBlock) {
      const groupElement = BlockUtils.getBlockElement(context.selectedBlock, containedBy);
      if (groupElement) {
        offsetX = groupElement.getIn([layout, "x"]) || 0;
        offsetY = groupElement.getIn([layout, "y"]) || 0;
        otherOffsetX = groupElement.getIn([otherLayout, "x"]) || 0;
        otherOffsetY = groupElement.getIn([otherLayout, "y"]) || 0;
      }
    }
    element[layout].x = canvasPosition[layout].x - offsetX;
    element[layout].y = canvasPosition[layout].y - offsetY;
    element[otherLayout].x = canvasPosition[otherLayout].x - otherOffsetX;
    element[otherLayout].y = canvasPosition[otherLayout].y - otherOffsetY;
  }
  DocumentStore.dispatch({
    type: COMMANDS.ADD_ELEMENT,
    payload: { element, isNew: true, maintainPosition: true, containedBy },
  });
  onClose();
};

const _addElement = (props, type) => _insertElementAtCursor(props, ModelFactory.getElement(type));

export const sharedActionHandlerMap = {
  [UNDO]: _undo,
  [REDO]: _redo,
  [COPY]: _copy,
  [PASTE_ELEMENT]: _pasteElement,
  [CUT]: _cut,
};

export const canvasActionHandlerMap = {
  [DELETE_CANVAS]: _removeBlock,
  [DUPLICATE_CANVAS]: _duplicateBlock,
  [RENAME_CANVAS]: _renameBlock,
  [LAYOUT_MOBILE_CANVAS]: _layoutMobile,
  [SELECT_CANVAS]: _selectBlock,
  [SELECT_SITE_CANVAS]: _selectSiteBlock,
};

export const elementActionHandlerMap = {
  [SWAP_IMAGE]: _swapImage,
  [RESET_IMAGE]: _resetImage,
  [CROP_IMAGE]: _cropImage,
  [DELETE_ELEMENT]: _removeElement,
  [DUPLICATE_ELEMENT]: _duplicateElement,
  [RENAME_ELEMENT]: _renameElement,
  [TOGGLE_LOCK_ELEMENT]: _toggleLock,
  [ALIGN_ELEMENT_TO_CANVAS_HORIZONTAL]: (props) => _alignElementToCanvas(props, ALIGN_CENTER),
  [ALIGN_ELEMENT_TO_CANVAS_VERTICAL]: (props) => _alignElementToCanvas(props, ALIGN_MIDDLE),
  [ALIGN_ELEMENT_TO_CANVAS_TOP]: (props) => _alignElementToCanvas(props, ALIGN_TOP),
  [ALIGN_ELEMENT_TO_CANVAS_BOTTOM]: (props) => _alignElementToCanvas(props, ALIGN_BOTTOM),
  [ALIGN_ELEMENT_TO_CANVAS_LEFT]: (props) => _alignElementToCanvas(props, ALIGN_LEFT),
  [ALIGN_ELEMENT_TO_CANVAS_RIGHT]: (props) => _alignElementToCanvas(props, ALIGN_RIGHT),

  [ALIGN_SELECTED_ELEMENTS_HORIZONTAL]: (props) => _alignSelectedElements(props, ALIGN_CENTER),
  [ALIGN_SELECTED_ELEMENTS_VERTICAL]: (props) => _alignSelectedElements(props, ALIGN_MIDDLE),
  [ALIGN_SELECTED_ELEMENTS_TOP]: (props) => _alignSelectedElements(props, ALIGN_TOP),
  [ALIGN_SELECTED_ELEMENTS_BOTTOM]: (props) => _alignSelectedElements(props, ALIGN_BOTTOM),
  [ALIGN_SELECTED_ELEMENTS_LEFT]: (props) => _alignSelectedElements(props, ALIGN_LEFT),
  [ALIGN_SELECTED_ELEMENTS_RIGHT]: (props) => _alignSelectedElements(props, ALIGN_RIGHT),

  [DISTRIBUTE_ELEMENTS_HORIZONTAL]: (props) =>
    _distributeElements(props, DISTRIBUTE_HORIZONTAL_SPACE),
  [DISTRIBUTE_ELEMENTS_VERTICAL]: (props) => _distributeElements(props, DISTRIBUTE_VERTICAL_SPACE),

  [BRING_TO_FRONT]: (props) => _moveToBound(props, LAYER_BOUND_FRONT),
  [SEND_TO_BACK]: (props) => _moveToBound(props, LAYER_BOUND_BACK),
  [BRING_FORWARD]: (props) => _moveLayers(props, DIRECTION_UP),
  [SEND_BACKWARD]: (props) => _moveLayers(props, DIRECTION_DOWN),

  [INSPECT_ELEMENT_JSON]: _inspectElementJSON,
};

export const groupActionHandlerMap = {
  [ADD_TO_GROUP]: _addToGroup,
  [REMOVE_FROM_GROUP]: _removeFromGroup,
  [UNGROUP_ELEMENTS]: _ungroupElements,
  [GROUP_ELEMENTS]: _groupElements,
};

export const addElementActionHandlerMap = {
  [ADD_TEXT_ELEMENT_TITLE]: (props) => _addElement(props, TEXT_STYLE_TITLE),
  [ADD_TEXT_ELEMENT_HEADING]: (props) => _addElement(props, TEXT_STYLE_HEADING),
  [ADD_TEXT_ELEMENT_SUBHEADING]: (props) => _addElement(props, TEXT_STYLE_SUBHEADING),
  [ADD_TEXT_ELEMENT_PARAGRAPH]: (props) => _addElement(props, TEXT_STYLE_PARAGRAPH),
  [ADD_ELEMENT_SIMPLE]: (props) => _addElement(props, ELEMENT_TYPE_SIMPLE),
  [ADD_ELEMENT_OVAL]: (props) => _addElement(props, ELEMENT_TYPE_OVAL),
  [ADD_ELEMENT_LINE]: (props) => _addElement(props, ELEMENT_TYPE_LINE),
  [ADD_ELEMENT_GRAPHIC]: (props) => _addElement(props, ELEMENT_TYPE_GRAPHIC),
  [ADD_ELEMENT_ICON]: (props) => _addElement(props, ELEMENT_TYPE_ICON),
  [ADD_ELEMENT_IFRAME]: (props) => _addElement(props, ELEMENT_TYPE_IFRAME),
  [ADD_ELEMENT_VIDEO]: (props) => _addElement(props, ELEMENT_TYPE_VIDEO),
  [ADD_ELEMENT_GALLERY]: (props) => _addElement(props, ELEMENT_TYPE_GALLERY),
  [ADD_ELEMENT_SOCIAL_GRID]: (props) => _addElement(props, ELEMENT_TYPE_SOCIAL_GRID),
  [ADD_ELEMENT_LOTTIE]: (props) => _addElement(props, ELEMENT_TYPE_LOTTIE),
  [ADD_ELEMENT_BUTTON]: (props) => _addElement(props, ELEMENT_TYPE_BUTTON),
  ...Object.fromEntries(
    FORM_FIELD_MENU_ITEMS.map(({ action, field }) => [
      action,
      (props) => _insertElementAtCursor(props, ModelFactory.getFormFieldElement(field)),
    ]),
  ),
  [ADD_FORM_SUBMIT_BUTTON]: (props) =>
    _insertElementAtCursor(props, ModelFactory.getSubmitButton()),
};
